/**
 * Connected history for the Value Estimator — evidence source 2 (PRD §5):
 * what the linked Jira issue, and issues like this item in the same project,
 * were sized at and actually took.
 *
 * Links come from entity_external_ids (item → Jira key, with the project
 * key); the read goes through the integrations engine's /jira/estimates.
 * Any failure degrades to "no connected history", never to an error that
 * stops an estimate — the switch is off by default and says what it sends.
 */

import { getSupabaseClient } from './supabaseClient';
import { readJiraEstimates } from './semanticConnectors/jiraSemanticApi';

// The shapes are the behavior's shared core (one copy with the server runtime).
import { hasSizingSignal, toConnectedLine } from '../../supabase/functions/_shared/estimateValueCore.ts';
export { hasSizingSignal, toConnectedLine, type ConnectedHistoryLine } from '../../supabase/functions/_shared/estimateValueCore.ts';
import type { ConnectedHistoryLine } from '../../supabase/functions/_shared/estimateValueCore.ts';

export async function loadConnectedHistory(params: {
  workspaceId: string;
  itemId: string;
  itemTitle: string;
  limit?: number;
}): Promise<ConnectedHistoryLine[]> {
  try {
    const { data: links } = await getSupabaseClient()
      .from('entity_external_ids')
      .select('external_id, external_project_key')
      .eq('workspace_id', params.workspaceId)
      .eq('entity_type', 'item')
      .eq('internal_id', params.itemId)
      .eq('connector_type', 'jira');
    const rows = (links ?? []) as Array<{ external_id: string; external_project_key: string | null }>;
    const keys = rows.map((r) => r.external_id).filter(Boolean);
    const projectKey = rows.find((r) => r.external_project_key)?.external_project_key ?? (keys[0]?.split('-')[0] ?? undefined);
    if (!keys.length && !projectKey) return [];
    const issues = await readJiraEstimates({
      workspaceId: params.workspaceId,
      keys,
      projectKey,
      search: params.itemTitle,
      maxResults: params.limit ?? 10,
    });
    return issues
      .map(toConnectedLine)
      .filter(hasSizingSignal)
      .sort((a, b) => Number(b.linked) - Number(a.linked))
      .slice(0, params.limit ?? 10);
  } catch (err) {
    console.warn('[ValueEstimator] connected history unavailable:', err instanceof Error ? err.message : err);
    return [];
  }
}
